<template>
	<v-row>
		<v-col cols="12">
			<v-card class="d-flex flex-column pb-5">
				<v-overlay
					:model-value="loading"
					persistent
					contained
					class="align-center justify-center"
				>
					<v-progress-circular
						indeterminate
						color="primary"
						size="48"
					/>
				</v-overlay>

				<v-toolbar color="primary" dark flat>
					<v-toolbar-title style="flex: 0 1 auto">
						Preferred Units
					</v-toolbar-title>
					<v-text-field
						v-model="searchTerm"
						prepend-inner-icon="mdi-text-search-variant"
						placeholder="Filter by measurement or alias"
						density="compact"
						variant="solo"
						flat
						rounded
						hide-details
						clearable
						bg-color="white"
						class="flex-grow-0 ml-4 units-toolbar-search"
					/>
				</v-toolbar>

				<preferred-unit-list
					:measurements="filteredMeasurements"
					:can-manage="canManage"
					@edit="openEdit"
					@create="openCreate"
					@changed="fetchMeasurements"
				/>
			</v-card>
		</v-col>

		<v-dialog
			v-model="editorOpen"
			max-width="620px"
			transition="fade-transition"
		>
			<unit-editor
				v-if="editorOpen"
				:mode="editorMode"
				:initial-data="editorData"
				:units="units"
				@dialog-close="editorOpen = false"
				@saved="onEditorSaved"
			/>
		</v-dialog>
	</v-row>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useMainStore } from "@/store";
import PreferredUnitList from "./PreferredUnitList.vue";
import UnitEditor from "./UnitEditor.vue";

export default {
	name: "PreferredUnitsBrowser",
	components: { PreferredUnitList, UnitEditor },
	data() {
		return {
			searchTerm: "",
			measurements: [],
			units: [],
			loading: false,
			editorOpen: false,
			editorMode: "create",
			editorData: {},
		};
	},
	computed: {
		...mapState(useMainStore, ["can"]),
		// The New / Edit / Delete controls are gated on manage_units (administrators
		// bypass via the store getter). This is a UX gate only - the backend also
		// enforces can:manage_units on every mutation route.
		canManage() {
			return this.can("manage_units");
		},
		// Client-side filter over the (small) measurement set: match the name or any
		// alias, case-insensitive. Avoids a round-trip per keystroke.
		filteredMeasurements() {
			const key = this.searchTerm?.trim().toLowerCase() ?? "";
			if (!key) return this.measurements;
			return this.measurements.filter((m) => {
				if (m.name?.toLowerCase().includes(key)) return true;
				return (m.aliases || []).some((a) =>
					a.toLowerCase().includes(key),
				);
			});
		},
	},
	mounted() {
		this.fetchUnits();
		this.fetchMeasurements();
	},
	methods: {
		...mapActions(useMainStore, ["showNotification"]),
		fetchMeasurements() {
			this.loading = true;
			this.$http
				.post("/dactyl-units/list", {})
				.then(({ data: responseData }) => {
					this.measurements = responseData.data || [];
				})
				.catch((error) => {
					console.error("Failed to fetch preferred units:", error);
					this.showNotification({
						status: "error",
						message: "Failed to load preferred units",
					});
				})
				.finally(() => {
					this.loading = false;
				});
		},
		fetchUnits() {
			this.$http
				.post("/units/list", {})
				.then(({ data: responseData }) => {
					this.units = responseData.data || [];
				})
				.catch((error) => {
					console.error("Failed to fetch units registry:", error);
					this.showNotification({
						status: "error",
						message: "Failed to load the units registry",
					});
				});
		},
		openCreate() {
			this.editorMode = "create";
			this.editorData = {};
			this.editorOpen = true;
		},
		openEdit(measurement) {
			this.editorMode = "edit";
			this.editorData = measurement;
			this.editorOpen = true;
		},
		onEditorSaved() {
			this.editorOpen = false;
			this.fetchMeasurements();
		},
	},
};
</script>

<style scoped>
.units-toolbar-search {
	min-width: 320px;
}
</style>
